export const STEPS = [
  { number: 1, key: "sales_opportunity_requirements", title: "Sales Opportunity & Client Requirements", owner: "bd", short: "Sales & Reqs" },
  { number: 2, key: "faculty_mapping_it_handoff", title: "Faculty Mapping & IT Handoff", owner: "pm", short: "Faculty & IT" },
  { number: 3, key: "it_cost_estimation_approval", title: "IT Cost Estimation & Approval", owner: "it", short: "Cost Approval" },
  { number: 4, key: "curriculum_delivery_planning", title: "Curriculum & Delivery Planning", owner: "pm", short: "Curriculum & Plan" },
  { number: 5, key: "infra_provisioning_validation", title: "Infrastructure Provisioning & Validation", owner: "it", short: "Infra Provisioning" },
  { number: 6, key: "welcome_delivery_feedback", title: "Welcome, Delivery & Feedback", owner: "pm", short: "Delivery & Feedback" },
  { number: 7, key: "it_billing", title: "Infrastructure Billing", owner: "it", short: "Billing" },
  { number: 8, key: "po_followup", title: "Purchase Order Follow-up", owner: "bd", short: "PO" },
  { number: 9, key: "invoice_payment_confirmation", title: "Invoice & Payment", owner: "finance", short: "Invoice & Payment" },
  { number: 10, key: "project_closure", title: "Project Closure", owner: "pm", short: "Closure" },
];

export const ROLE_LABELS = {
  admin: "Admin",
  bd: "Business Development",
  pm: "Program Management",
  it: "IT Team",
  finance: "Accounts / Finance",
  hr: "HR Team",
  client: "Client",
  faculty: "Faculty",
};

export const ROLE_COLORS = {
  admin: "bg-slate-100 text-slate-800 border-slate-300",
  bd: "bg-blue-50 text-blue-800 border-blue-200",
  pm: "bg-indigo-50 text-indigo-800 border-indigo-200",
  it: "bg-cyan-50 text-cyan-800 border-cyan-200",
  finance: "bg-emerald-50 text-emerald-800 border-emerald-200",
  hr: "bg-amber-50 text-amber-800 border-amber-200",
  client: "bg-fuchsia-50 text-fuchsia-800 border-fuchsia-200",
  faculty: "bg-teal-50 text-teal-800 border-teal-200",
};

export const STATUS_COLORS = {
  pending: "bg-slate-100 text-slate-700 border-slate-200",
  in_progress: "bg-blue-100 text-blue-800 border-blue-300",
  completed: "bg-emerald-100 text-emerald-800 border-emerald-300",
  blocked: "bg-red-100 text-red-800 border-red-300",
  overdue: "bg-amber-100 text-amber-900 border-amber-300",
};

export const STATUS_DOT = {
  pending: "bg-slate-300",
  in_progress: "bg-blue-600",
  completed: "bg-emerald-600",
  blocked: "bg-red-600",
  overdue: "bg-amber-500",
};

// True unless Step 2 explicitly recorded client-provided infra — matches the backend's
// _is_nexus_infra convention: anything other than a literal "No" (including not yet
// answered) defaults to Nexus-provisioned infra.
export function isNexusInfra(program) {
  const step2 = program?.steps?.find((s) => s.step_number === 2);
  return step2?.form_data?.infra_provisioning_by_nexusiq !== "No";
}

// Licensing is opt-in: only an explicit "Yes" on Step 2 means NexusIQ supplies licences.
export function isNexusLicensing(program) {
  const step2 = program?.steps?.find((s) => s.step_number === 2);
  return step2?.form_data?.licensing_by_nexusiq === "Yes";
}

export const STEP_FIELDS = {
  1: [
    { name: "client_contact", label: "Client Contact Person", type: "text" },
    { name: "client_email", label: "Client Contact Email", type: "email" },
    { name: "estimated_revenue", label: "Estimated Revenue (INR)", type: "number" },
    { name: "requirements_summary", label: "Requirements Summary", type: "textarea" },
    { name: "technology", label: "Skills / Technology", type: "multiselect_search", optionsSource: "skills" },
    { name: "delivery_mode", label: "Delivery Mode", type: "select", options: ["Online", "Hybrid", "Onsite"] },
    { name: "hours_per_day", label: "Hours per Day", type: "number" },
    { name: "time_slot_start", label: "Daily Time Slot Start", type: "time" },
    { name: "time_slot_end", label: "Daily Time Slot End", type: "time" },
    { name: "duration_hours", label: "Total Duration (hours)", type: "number" },
  ],
  2: [
    { name: "faculty_names", label: "Faculty / Trainer(s)", type: "faculty_multiselect", optionsSource: "faculty" },
    { name: "faculty_expertise", label: "Faculty Expertise Areas (Map Skills)", type: "checkbox_group", optionsSource: "skills" },
    { name: "availability_date", label: "Availability Date", type: "date" },
    { name: "time_slot_start", label: "Availability — Time Slot Start (IST)", type: "time" },
    { name: "time_slot_end", label: "Availability — Time Slot End (IST)", type: "time" },
    { name: "client_approval", label: "Client Approval", type: "radio", options: ["Yes", "No", "Pending"] },
    { name: "faculty_notes", label: "Notes", type: "textarea" },
    { name: "infra_provisioning_by_nexusiq", label: "Infra Provisioning by NexusIQ?", type: "select", options: ["Yes", "No"] },
    { name: "infra_start_date", label: "Infra Start Date", type: "date", showIf: (form) => form.infra_provisioning_by_nexusiq !== "No" },
    { name: "infra_end_date", label: "Infra End Date", type: "date", showIf: (form) => form.infra_provisioning_by_nexusiq !== "No" },
    { name: "infra_type", label: "Infra Type", type: "select", options: ["Cloud", "On-prem", "GPU", "CPU"], showIf: (form) => form.infra_provisioning_by_nexusiq !== "No", requiredIf: (form) => form.infra_provisioning_by_nexusiq === "Yes" },
    { name: "instance_count", label: "Number of Instances", type: "number", showIf: (form) => form.infra_provisioning_by_nexusiq !== "No", requiredIf: (form) => form.infra_provisioning_by_nexusiq === "Yes" },
    { name: "software_stack", label: "Software / Frameworks Required", type: "checkbox_group", optionsSource: "software", showIf: (form) => form.infra_provisioning_by_nexusiq !== "No", requiredIf: (form) => form.infra_provisioning_by_nexusiq === "Yes" },
    { name: "needed_by", label: "Infra Required Date", type: "date", showIf: (form) => form.infra_provisioning_by_nexusiq !== "No", requiredIf: (form) => form.infra_provisioning_by_nexusiq === "Yes" },
    { name: "shared_date", label: "Date Shared", type: "date", showIf: (form) => form.infra_provisioning_by_nexusiq !== "No", requiredIf: (form) => form.infra_provisioning_by_nexusiq === "Yes" },
    // Licences can be supplied by NexusIQ with or without infra. "Yes" adds licence cost
    // fields to Step 3 and licence provisioning/testing fields to Step 5.
    { name: "licensing_by_nexusiq", label: "Licensing part of this program?", type: "select", options: ["Yes", "No"] },
    { name: "license_requirements", label: "Licences Required (software / tool, count, duration)", type: "textarea", showIf: (form) => form.licensing_by_nexusiq === "Yes" },
    { name: "handoff_notes", label: "Handoff Notes", type: "textarea" },
  ],
  3: [
    { name: "estimated_cost", label: "Estimated Infra Cost (INR)", type: "number", showIf: (form, program) => isNexusInfra(program) },
    { name: "setup_cost", label: "Setup Cost (INR)", type: "number", showIf: (form, program) => isNexusInfra(program) },
    { name: "actuals_cost", label: "Actuals (INR)", type: "number", showIf: (form, program) => isNexusInfra(program) },
    { name: "license_details", label: "Licence Details (software / tool, edition, duration)", type: "textarea", showIf: (form, program) => isNexusLicensing(program) },
    { name: "license_count", label: "Number of Licences", type: "number", showIf: (form, program) => isNexusLicensing(program) },
    { name: "license_cost", label: "Licence Cost (INR)", type: "number", showIf: (form, program) => isNexusLicensing(program) },
    { name: "margin_percent", label: "Margin (%)", type: "number" },
    // Total = Setup + Base + Licence + Margin% of (Base + Licence), where Base = Actuals once
    // entered, else the Estimated Infra Cost; Licence counts only when Step 2 says licensing is
    // "Yes". Must stay in sync with _compute_step3_cost in backend/server.py.
    { name: "total_cost", label: "Total Cost (INR)", type: "computed", compute: (form, program) => {
        const has = (v) => v !== undefined && v !== null && v !== "";
        const licensing = isNexusLicensing(program);
        if (!has(form.setup_cost) && !has(form.actuals_cost) && !has(form.estimated_cost) && !(licensing && has(form.license_cost))) return "";
        const setup = parseFloat(form.setup_cost) || 0;
        const base = has(form.actuals_cost) ? (parseFloat(form.actuals_cost) || 0) : (parseFloat(form.estimated_cost) || 0);
        const license = licensing ? (parseFloat(form.license_cost) || 0) : 0;
        const margin = parseFloat(form.margin_percent) || 0;
        return (setup + base + license + ((base + license) * margin) / 100).toFixed(2);
      } },
    { name: "approval_status", label: "Approval Status", type: "select", options: ["Pending", "Approved", "Rejected"] },
  ],
  4: [
    { name: "hours_per_day", label: "Hours per Day", type: "number" },
    { name: "delivery_time_start", label: "Delivery Time Slot Start", type: "time" },
    { name: "delivery_time_end", label: "Delivery Time Slot End", type: "time" },
    { name: "duration_hours", label: "Total Duration (hours)", type: "number" },
    { name: "batch_details", label: "Batch Details", type: "textarea" },
  ],
  5: [
    { name: "infra_used", label: "Nexus Infra Used", type: "infra_select", optionsSource: "infra", showIf: (form, program) => isNexusInfra(program) },
    { name: "infra_provided_to_faculty", label: "Infra Provided to Faculty", type: "select", options: ["Yes", "No", "NA"] },
    { name: "labs_created", label: "Labs Created", type: "select", options: ["Yes", "No", "Partial"], showIf: (form, program) => isNexusInfra(program) },
    { name: "session_links", label: "Session Links", type: "textarea" },
    { name: "user_accounts_created", label: "User Accounts Created", type: "select", options: ["Not Started", "In Progress", "Completed"], showIf: (form, program) => isNexusInfra(program) },
    { name: "software_installation", label: "Software Installation Status", type: "select", options: ["Not Started", "In Progress", "Completed"], showIf: (form, program) => isNexusInfra(program) },
    { name: "zoom_invites_sent", label: "Zoom Invites Sent", type: "select", options: ["Yes", "No", "Pending"] },
    { name: "faculty_testing_status", label: "Faculty Testing", type: "select", options: ["Not Tested", "Passed", "Failed"], showIf: (form, program) => isNexusInfra(program) },
    { name: "client_network_testing_status", label: "Client Network Testing", type: "select", options: ["Not Tested", "Passed", "Failed"], showIf: (form, program) => isNexusInfra(program) },
    { name: "dry_run_status", label: "Dry Run", type: "select", options: ["Not Done", "Successful", "Issues Found"], showIf: (form, program) => isNexusInfra(program) },
    { name: "infra_ready", label: "Infrastructure Ready?", type: "select", options: ["Yes", "No"], showIf: (form, program) => isNexusInfra(program) },
    { name: "issue_description", label: "Issue Description (if any)", type: "textarea", showIf: (form, program) => isNexusInfra(program) },
    { name: "resolution_notes", label: "Resolution Notes (if any)", type: "textarea", showIf: (form, program) => isNexusInfra(program) },
    { name: "infra_extension_date", label: "Infra Extension Date", type: "date", showIf: (form, program) => isNexusInfra(program) },
    { name: "infra_closure_status", label: "Infra Closure Status", type: "select", options: ["Open", "Closed"], showIf: (form, program) => isNexusInfra(program) },
    { name: "licenses_provisioned", label: "Licences Provisioned", type: "select", options: ["Not Started", "In Progress", "Completed"], showIf: (form, program) => isNexusLicensing(program) },
    { name: "license_testing_status", label: "Licence Testing", type: "select", options: ["Not Tested", "Passed", "Failed"], showIf: (form, program) => isNexusLicensing(program) },
    { name: "license_provisioning_notes", label: "Licence Provisioning Notes (keys / accounts shared, issues)", type: "textarea", showIf: (form, program) => isNexusLicensing(program) },
  ],
  6: [
    { name: "batch_recipients", label: "Batch Email Recipients (comma-separated)", type: "textarea" },
    { name: "faculty_recipients", label: "Faculty Email Recipients (comma-separated)", type: "textarea" },
    { name: "lms_invited", label: "LMS Invited", type: "select", options: ["Yes", "No"] },
    { name: "training_start", label: "Actual Training Start", type: "date" },
    { name: "training_end", label: "Actual Training End", type: "date" },
    { name: "attendance_summary", label: "Attendance Summary", type: "textarea" },
    { name: "session_monitoring_notes", label: "Session Monitoring Notes", type: "textarea" },
    { name: "moderator_name", label: "Moderator Name", type: "text" },
    { name: "participant_feedback_summary", label: "Training Feedback Summary (by Faculty)", type: "textarea" },
    { name: "faculty_feedback_summary", label: "Faculty Feedback Summary (by Trainees)", type: "textarea" },
    { name: "nps_score", label: "NPS Score", type: "number" },
    { name: "shared_summary", label: "Feedback Summary Shared with BD", type: "select", options: ["Yes", "No"] },
    { name: "shared_date", label: "Date Shared", type: "date" },
  ],
  7: [
    { name: "billing_amount", label: "Billing Amount (INR)", type: "number" },
    { name: "billing_details", label: "Billing Break-up / Notes", type: "textarea" },
    { name: "bd_recipient", label: "BD Recipient Email", type: "email" },
  ],
  8: [
    { name: "po_number_training", label: "PO Number — Training", type: "text", required: true },
    { name: "po_amount_training", label: "PO Amount — Training (INR)", type: "number", required: true },
    { name: "po_number_infra", label: "PO Number — Infra", type: "text", showIf: (form, program) => isNexusInfra(program), requiredIf: (form, program) => isNexusInfra(program) },
    { name: "po_amount_infra", label: "PO Amount — Infra (INR)", type: "number", showIf: (form, program) => isNexusInfra(program), requiredIf: (form, program) => isNexusInfra(program) },
    { name: "infra_closure_confirmation", label: "Infra Closure Confirmation", type: "select", options: ["Yes", "No"], showIf: (form, program) => isNexusInfra(program), requiredIf: (form, program) => isNexusInfra(program) },
    { name: "po_date", label: "PO Date", type: "date", required: true },
    { name: "followup_notes", label: "Follow-up Notes", type: "textarea" },
  ],
  9: [
    { name: "invoice_number", label: "Invoice Number", type: "text", required: true },
    { name: "invoice_amount", label: "Invoice Amount (INR)", type: "number", required: true },
    { name: "invoice_date", label: "Invoice Date", type: "date", required: true },
    { name: "followup_notes", label: "Follow-up Notes", type: "textarea" },
    { name: "expected_payment_date", label: "Expected Payment Date", type: "date", required: true },
    { name: "payment_received_date", label: "Payment Received Date", type: "date", required: true },
    { name: "payment_amount", label: "Payment Amount (INR)", type: "number", required: true },
    { name: "payment_reference", label: "Payment Reference / UTR", type: "text", required: true },
  ],
  10: [
    { name: "certificates_issued", label: "Certificates Issued", type: "select", options: ["Yes", "No"], required: true },
    { name: "documentation_link", label: "Documentation Link", type: "text", required: true },
  ],
};
